import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import {
  Folder,
  File as FileIcon,
  Grid,
  List,
  Plus,
  MoreVertical,
  ChevronRight,
  Search,
  Download,
  Trash2,
  FileText,
  Image as ImageIcon,
  Video,
  FileCode,
  Loader2,
  Settings,
  FolderTree,
  X,
  Filter,
  CheckCircle,
  Clock,
  XCircle,
  Globe,
  Youtube,
  Upload,
  RefreshCw,
  ChevronUp,
  ChevronDown,
} from "lucide-react";
import { DndProvider, useDrag, useDragLayer, useDrop } from "react-dnd";
import { getEmptyImage, HTML5Backend } from "react-dnd-html5-backend";
import api from "../../api";
import { showSuccessToast, showErrorToast } from "../../utils/toastUtils";
import "../../css/RepoOrganizer.css";
import { useTableSort } from "../../hooks/useTableSort";
import { SortableHeader } from "../../components/SortableHeader";
import changeStatusIcon from "../../assets/icons/changeStatusIcon.png";

const formatBytes = (bytes, decimals = 1) => {
  if (!+bytes) return "0 Bytes";
  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ["Bytes", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(dm))} ${sizes[i]}`;
};

const ITEM_TYPE = "REPO_ITEM";

const canModifyRepoItem = (item, role) =>
  role === "admin" ||
  (item.ownerType !== "admin" &&
    item.mode !== "global" &&
    item.mode !== "group");

const RepoDragPreview = () => {
  const { item, isDragging, currentOffset } = useDragLayer((monitor) => ({
    item: monitor.getItem(),
    isDragging: monitor.isDragging(),
    currentOffset: monitor.getSourceClientOffset(),
  }));

  if (!isDragging || !currentOffset || !item) {
    return null;
  }

  const dragCount = item.count || 1;
  const isFolderDrag = item.items?.every((dragItem) => dragItem.type === "folder");

  return (
    <div
      className="repo-drag-preview-layer"
      style={{
        transform: `translate(${currentOffset.x + 14}px, ${currentOffset.y + 14}px)`,
      }}
    >
      <div className="repo-drag-preview-stack" />
      <div className="repo-drag-preview-card">
        {isFolderDrag ? (
          <Folder size={16} className="repo-folder-icon" />
        ) : (
          <FileIcon size={16} className="repo-file-icon" />
        )}
        <span className="repo-drag-preview-name" title={item.name}>
          {item.name}
        </span>
      </div>
      {dragCount > 1 && (
        <span className="repo-drag-preview-badge">{dragCount}</span>
      )}
    </div>
  );
};

// --- Draggable/Droppable Item Component ---
const DraggableItem = ({
  item,
  viewMode,
  onNavigate,
  onToggleSelect,
  onAction,
  isSelected,
  getFileIcon,
  onMove,
  role,
  onApprovalStatusChange,
  dragSelectionRef,
  selectedDragItems,
}) => {
  // Check if user can move/modify this item
  // In the refined system, admins can modify everything. Regular users can modify personal items only.
  const canModify = canModifyRepoItem(item, role);
  const dragItems =
    isSelected && selectedDragItems.length > 0 ? selectedDragItems : [item];

  const [{ isDragging }, drag, preview] = useDrag(() => ({
    type: ITEM_TYPE,
    item: () => ({
      id: item.id,
      type: item.type,
      name: item.name,
      items: dragItems,
      count: dragItems.length,
    }),
    canDrag: canModify,
    collect: (monitor) => ({
      isDragging: !!monitor.isDragging(),
    }),
  }), [item, dragItems, canModify]);

  useEffect(() => {
    preview(getEmptyImage(), { captureDraggingState: true });
  }, [preview]);

  const getDraggedItems = (draggedItem) => draggedItem.items || [draggedItem];

  const [{ isOver }, drop] = useDrop({
    accept: ITEM_TYPE,
    canDrop: (draggedItem) => {
      const draggedItems = getDraggedItems(draggedItem);
      const draggedIds = new Set(draggedItems.map((dragItem) => dragItem.id));
      return item.type === "folder" && !draggedIds.has(item.id) && canModify;
    },
    drop: (draggedItem) => onMove(getDraggedItems(draggedItem), item.id),
    collect: (monitor) => ({
      isOver: !!monitor.isOver(),
    }),
  });

  const combinedRef = (node) => {
    drag(node);
    if (item.type === "folder") drop(node);
  };

  if (viewMode === "grid") {
    return (
      <div
        ref={combinedRef}
        data-item-id={String(item.id)}
        className={`repo-card ${isSelected ? "selected" : ""} ${isOver ? "drop-target" : ""} ${!canModify ? "read-only" : ""}`}
        style={{ opacity: isDragging ? 0.5 : 1 }}
        onClick={(e) => {
          if (dragSelectionRef.current) {
            return;
          }
          if (item.type === "file") {
            onToggleSelect(item.id);
          }
        }}
        onDoubleClick={(e) => {
          if (item.type === "folder") {
            onNavigate(item.id, item.name);
          }
        }}
      >
        <div
          className="repo-card-checkbox"
          onClick={(e) => {
            e.stopPropagation();
            onToggleSelect(item.id);
          }}
        >
          <input
            type="checkbox"
            checked={isSelected}
            readOnly
            style={{ cursor: "pointer" }}
          />
        </div>

        <div className="repo-card-icon">
          {item.type === "folder" ? (
            <Folder
              className="repo-folder-icon"
              size={48}
              fill="currentColor"
              fillOpacity={0.2}
            />
          ) : (
            getFileIcon(item, 48)
          )}
        </div>

        <div className="repo-card-content">
          <div className="repo-card-name" title={item.name}>
            {item.name}
          </div>
          <div className="repo-card-details">
            <span className="repo-card-type">
              {item.type === "folder"
                ? "Folder"
                : item.name.split(".").pop().toUpperCase()}
            </span>
            {item.type === "file" && item.size && (
              <span className="repo-card-size">• {formatBytes(item.size)}</span>
            )}
          </div>
          <div className="repo-card-date">
            {new Date(item.updatedAt || item.createdAt).toLocaleDateString()}
          </div>
        </div>

        <div className="repo-card-footer">
          {item.type === "file" && (
            <span
              className={`badge ${item.fileStatus === "1" ? "bg-success" : "bg-secondary"}`}
              style={{ fontSize: "0.6rem" }}
            >
              {item.fileStatus === "1" ? "Active" : "Inactive"}
            </span>
          )}
          {item.mode === "global" && (
            <span className="badge bg-info" style={{ fontSize: "0.6rem" }}>
              Global
            </span>
          )}
        </div>

        <div className="repo-card-actions">
          <div className="dropdown border-0">
            <button
              className="repo-action-dot bg-transparent border-0"
              data-bs-toggle="dropdown"
              onClick={(e) => e.stopPropagation()}
            >
              <MoreVertical size={14} />
            </button>
            <ul className="dropdown-menu">
              {item.type === "file" && (
                <li>
                  <button
                    className="dropdown-item "
                    style={{ maxWidth: "100%" }}
                    onClick={(e) => {
                      e.stopPropagation();
                      onAction("download", item);
                    }}
                  >
                    <Download size={14} /> Download
                  </button>
                </li>
              )}
              {item.type === "file" &&
                (item.ownerType === "admin" || role === "admin" || item.isPersonalSpace) && (
                  <li>
                    <button
                      className="dropdown-item "
                      style={{ maxWidth: "100%" }}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (item.isPersonalSpace) {
                          if (item.fileStatus === "1") {
                             onAction("process", item);
                          } else {
                             onAction("revert", item);
                          }
                        } else {
                          onAction("status", item);
                        }
                      }}
                    >
                      {item.isPersonalSpace ? (
                        item.fileStatus === "1" ? (
                          <>
                             <Upload size={14} className="icon-image" /> Train File
                          </>
                        ) : (
                          <>
                             <RefreshCw size={14} className="icon-image" /> Restore File
                          </>
                        )
                      ) : (
                        <>
                          <Settings size={14} className="icon-image" /> Change Status
                        </>
                      )}
                    </button>
                  </li>
                )}

              {(item.isPersonalSpace || (item.mode === "personal" && item.ownerType === "user")) && (
                <li>
                  <button
                    className="dropdown-item text-danger"
                    onClick={(e) => {
                      e.stopPropagation();
                      onAction("delete", item);
                    }}
                  >
                    <Trash2 size={14} className="icon-image" /> {item.fileStatus === "0" ? "Permanent Delete" : "Delete"}
                  </button>
                </li>
              )}
              {canModify && (
                <>
                  <li>
                    <button
                      className="dropdown-item "
                      style={{ maxWidth: "100%" }}
                      onClick={(e) => {
                        e.stopPropagation();
                        onAction("move", item);
                      }}
                    >
                      <FolderTree size={14} /> Move
                    </button>
                  </li>
                  {/* <li>
                    <hr className="dropdown-divider" />
                  </li> */}
                  {/* <li>
                    <button
                      className="dropdown-item text-danger"
                      style={{ maxWidth: "100%" }}
                      onClick={(e) => {
                        e.stopPropagation();
                        onAction("delete", item);
                      }}
                    >
                      <Trash2 size={14} /> Delete
                    </button>
                  </li> */}
                </>
              )}
            </ul>
          </div>
        </div>
      </div>
    );
  }

  return (
    <tr
      ref={combinedRef}
      data-item-id={String(item.id)}
      className={`repo-list-row ${isSelected ? "selected" : ""} ${isOver ? "drop-target" : ""} ${!canModify ? "read-only" : ""}`}
      style={{ opacity: isDragging ? 0.5 : 1 }}
    >
      <td style={{ width: "40px" }} onClick={(e) => e.stopPropagation()}>
        <input
          type="checkbox"
          checked={isSelected}
          onChange={() => onToggleSelect(item.id)}
          style={{ cursor: "pointer" }}
        />
      </td>
      <td
        onClick={(e) => {
          if (dragSelectionRef.current) {
            return;
          }
          if (item.type === "file") {
            onToggleSelect(item.id);
          }
        }}
        onDoubleClick={(e) => {
          if (item.type === "folder") {
            onNavigate(item.id, item.name);
          }
        }}
        style={{ cursor: "pointer" }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
          {item.type === "folder" ? (
            <Folder size={18} className="repo-folder-icon" />
          ) : (
            getFileIcon(item, 18)
          )}
          <span
            style={{
              maxWidth: "250px",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
            title={item.name}
          >
            {item.name}
          </span>
          {/* {item.mode === "global" && (
            <span className="badge bg-info ms-2" style={{ fontSize: "0.6rem" }}>
              Global
            </span>
          )} */}
        </div>
      </td>
      <td>
        {item.type === "folder"
          ? "Folder"
          : item.name.split(".").pop().toUpperCase()}
      </td>
      <td>
        {item.type === "file"
          ? item.size
            ? formatBytes(item.size)
            : "0 Bytes"
          : "--"}
      </td>
      <td>
        {item.type === "file" &&
          (role === "admin" ? (
            <select
              className={`badge ${
                item.approvalStatus === "approved"
                  ? "bg-success"
                  : item.approvalStatus === "duplicate"
                    ? "bg-secondary"
                    : item.approvalStatus === "pending"
                      ? "bg-warning"
                      : "bg-danger"
              }`}
              value={item.approvalStatus || "pending"}
              onChange={(e) => {
                e.stopPropagation();
                onApprovalStatusChange &&
                  onApprovalStatusChange(item.id, e.target.value);
              }}
              onClick={(e) => e.stopPropagation()}
              style={{ cursor: "pointer", border: "none", outline: "none" }}
            >
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
              <option value="duplicate">Duplicate</option>
            </select>
          ) : (
            <span
              className={`badge ${
                item.approvalStatus === "approved"
                  ? "bg-success"
                  : item.approvalStatus === "duplicate"
                    ? "bg-secondary"
                    : item.approvalStatus === "pending"
                      ? "bg-warning"
                      : "bg-danger"
              }`}
            >
              {(item.approvalStatus || "Pending").charAt(0).toUpperCase() +
                (item.approvalStatus || "Pending").slice(1)}
            </span>
          ))}
      </td>
      <td>
        {item.type === "file" && (
          <span
            className={`badge ${item.fileStatus === "1" ? "bg-success" : "bg-secondary"}`}
          >
            {item.fileStatus === "1" ? "Active" : "Inactive"}
          </span>
        )}
      </td>
      <td>
        {item.createdAt ? new Date(item.createdAt).toLocaleDateString() : "N/A"}
      </td>
      <td>
        {item.updatedAt ? new Date(item.updatedAt).toLocaleDateString() : "N/A"}
      </td>
      {role === "admin" && <td>{item.uploadedBy || "N/A"}</td>}
      <td>
        <div
          style={{
            display: "flex",
            gap: "0.5rem",
            alignItems: "center",
            justifyContent: "flex-end",
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {item.type === "file" && (
            <Download
              size={16}
              onClick={() => onAction("download", item)}
              style={{ cursor: "pointer" }}
              title="Download"
            />
          )}
          {item.type === "file" && (item.ownerType === "admin" || role === "admin" || item.isPersonalSpace) && (
            item.isPersonalSpace ? (
              item.fileStatus === "1" ? (
                <Upload
                  size={16}
                  onClick={() => onAction("process", item)}
                  style={{ cursor: "pointer" }}
                  title="Train File"
                />
              ) : (
                <RefreshCw
                  size={16}
                  onClick={() => onAction("revert", item)}
                  style={{ cursor: "pointer" }}
                  title="Restore File"
                />
              )
            ) : (
              <img
                src={changeStatusIcon}
                className="icon-image"
                style={{
                  width: "16px",
                  height: "16px",
                  cursor: (item.mode?.toLowerCase() === "global" || item.approvalStatus === "duplicate") ? "not-allowed" : "pointer",
                }}
                onClick={() => {
                  if (!(item.mode?.toLowerCase() === "global" || item.approvalStatus === "duplicate")) {
                    onAction("status", item);
                  }
                }}
                title="Change Status"
              />
            )
          )}
          {(item.isPersonalSpace || canModify) && (
            <Trash2
              size={16}
              className="text-danger"
              onClick={() => onAction("delete", item)}
              style={{ cursor: "pointer" }}
              title={item.isPersonalSpace && item.fileStatus === "0" ? "Permanent Delete" : "Delete"}
            />
          )}
          {canModify && (
            <FolderTree
              size={16}
              className="icon-image"
              onClick={() => onAction("move", item)}
              style={{ cursor: "pointer" }}
              title="Move"
            />
          )}
        </div>
      </td>
    </tr>
  );
};


const RepoOrganizer = ({ role = "user" }) => {
  // --- State ---
  const [viewMode, setViewMode] = useState("list");
  const [items, setItems] = useState([]);
  const [currentFolder, setCurrentFolder] = useState(null);
  const [breadcrumbs, setBreadcrumbs] = useState([{ id: null, name: "Root" }]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedItems, setSelectedItems] = useState(new Set());
  const [typeFilters, setTypeFilters] = useState(["all"]);
  const [isLoading, setIsLoading] = useState(false);
  const [repoView, setRepoView] = useState(
    role === "admin" ? "global" : "personal",
  ); // Default based on role
  const [statusFilter, setStatusFilter] = useState("Saved"); // "Saved" or "Deleted"

  // Modal states
  const [deleteModal, setDeleteModal] = useState({ show: false, items: [] });
  const [moveModal, setMoveModal] = useState({
    show: false,
    items: [],
    targetFolderId: null,
  });
  const [createFolderModal, setCreateFolderModal] = useState({
    show: false,
    name: "",
  });
  const [dayFilter, setDayFilter] = useState("All");
  const [entriesPerPage, setEntriesPerPage] = useState(50);
  const [currentPage, setCurrentPage] = useState(1);
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  const [tempSortOrder, setTempSortOrder] = useState("Descending");
  const [tempDayFilter, setTempDayFilter] = useState("All");
  const [tempFolderSort, setTempFolderSort] = useState("Top");
  const [appliedSortOrder, setAppliedSortOrder] = useState("Descending");
  const [appliedFolderSort, setAppliedFolderSort] = useState("Top");

  const filterDropdownRef = useRef(null);
  const containerRef = useRef(null);
  const selectionStartRef = useRef({ x: 0, y: 0 });
  const selectionActiveRef = useRef(false);
  const dragSelectionRef = useRef(false);
  const [selectionRect, setSelectionRect] = useState(null);
  const fetchAbortControllerRef = useRef(null);

  // Upload states
  const [showFirstModal, setShowFirstModal] = useState(false);
  const [showSummaryModal, setShowSummaryModal] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [uploadMode, setUploadMode] = useState("Global");
  const [showUploadModeDropdown, setShowUploadModeDropdown] = useState(false);
  const [dataName, setDataName] = useState("");
  const [description, setDescription] = useState("");
  const [summaryDataList, setSummaryDataList] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loadingIndex, setLoadingIndex] = useState(null);

  const fileInputRef = useRef(null);
  const dropdownRef = useRef(null);

  const isInteractiveSelectionTarget = (target) => {
    return !!target.closest(
      "button, input, select, textarea, label, .repo-action-dot, .dropdown-menu, .repo-card-actions, .repo-search-container, .repo-modal, .repo-modal-overlay, .repo-filter-dropdown-wrapper",
    );
  };

  const selectItemsInRect = useCallback((rect) => {
    if (!containerRef.current || !rect) return;
    const selected = new Set();
    containerRef.current
      .querySelectorAll("[data-item-id]")
      .forEach((node) => {
        const itemRect = node.getBoundingClientRect();
        if (
          itemRect.right >= rect.left &&
          itemRect.left <= rect.right &&
          itemRect.bottom >= rect.top &&
          itemRect.top <= rect.bottom
        ) {
          selected.add(node.dataset.itemId);
        }
      });
    setSelectedItems(selected);
  }, []);

  const updateSelection = useCallback(
    (clientX, clientY) => {
      if (!selectionActiveRef.current || !containerRef.current) return;
      const containerRect = containerRef.current.getBoundingClientRect();
      const startX = selectionStartRef.current.x;
      const startY = selectionStartRef.current.y;
      const left = Math.min(startX, clientX);
      const top = Math.min(startY, clientY);
      const width = Math.abs(clientX - startX);
      const height = Math.abs(clientY - startY);
      const rect = {
        left: left - containerRect.left,
        top: top - containerRect.top,
        width,
        height,
        right: left + width,
        bottom: top + height,
      };
      setSelectionRect(rect);
      selectItemsInRect(rect);
    },
    [selectItemsInRect],
  );

  const handleMouseDown = (event) => {
    if (event.button !== 0) return;
    if (!containerRef.current || !containerRef.current.contains(event.target)) return;
    if (isInteractiveSelectionTarget(event.target)) return;

    const targetCard = event.target.closest(".repo-card, .repo-list-row");
    if (targetCard) {
      return;
    }

    selectionActiveRef.current = true;
    dragSelectionRef.current = false;
    selectionStartRef.current = { x: event.clientX, y: event.clientY };
    const containerRect = containerRef.current.getBoundingClientRect();
    setSelectionRect({
      left: event.clientX - containerRect.left,
      top: event.clientY - containerRect.top,
      width: 0,
      height: 0,
      right: event.clientX,
      bottom: event.clientY,
    });
    event.preventDefault();
    event.stopPropagation();
  };

  useEffect(() => {
    const handleMouseMove = (event) => {
      if (!selectionActiveRef.current) return;
      updateSelection(event.clientX, event.clientY);
      if (
        Math.abs(event.clientX - selectionStartRef.current.x) > 5 ||
        Math.abs(event.clientY - selectionStartRef.current.y) > 5
      ) {
        dragSelectionRef.current = true;
      }
    };

    const handleMouseUp = (event) => {
      if (!selectionActiveRef.current) return;
      updateSelection(event.clientX, event.clientY);
      selectionActiveRef.current = false;
      setSelectionRect(null);
      window.requestAnimationFrame(() => {
        dragSelectionRef.current = false;
      });
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [updateSelection]);

  // Close dropdown on outside click
  useEffect(() => {
    function handleClickOutside(event) {
      if (
        filterDropdownRef.current &&
        !filterDropdownRef.current.contains(event.target)
      ) {
        setShowFilterDropdown(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside, true);
    return () =>
      document.removeEventListener("mousedown", handleClickOutside, true);
  }, []);

  const handleApplyFilter = () => {
    setDayFilter(tempDayFilter);
    setAppliedSortOrder(tempSortOrder);
    setAppliedFolderSort(tempFolderSort);
    setShowFilterDropdown(false);
    setCurrentPage(1);
  };

  const handleResetFilter = () => {
    setTempSortOrder("Descending");
    setTempDayFilter("All");
    setTempFolderSort("Top");
    setDayFilter("All");
    setAppliedSortOrder("Descending");
    setAppliedFolderSort("Top");
    setShowFilterDropdown(false);
    setCurrentPage(1);
  };

  // --- Derived Data ---
  const filteredItems = useMemo(() => {
    let viewFiltered = items.filter((item) => {
      // If we are in "Global Repo" tab
      if (repoView === "global") {
        return (
          item.ownerType === "admin" ||
          item.mode === "global" ||
          item.mode === "group"
        );
      }
      // If we are in "Personal Repo" tab
      if (repoView === "personal") {
        return (
          item.ownerType !== "admin" &&
          item.mode !== "global" &&
          item.mode !== "group"
        );
      }
      // If we are in "My personal space" tab
      if (repoView === "personal_space") {
        return item.isPersonalSpace === true;
      }
      return true;
    });

    let result = viewFiltered.filter((item) => {
      // Search filter
      if (
        searchTerm &&
        !item.name.toLowerCase().includes(searchTerm.toLowerCase())
      )
        return false;

      // Day Filter
      if (dayFilter && dayFilter !== "All") {
        const itemDate = new Date(item.updatedAt || item.createdAt);
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        if (dayFilter === "Today") {
          if (itemDate < today) return false;
        } else if (dayFilter === "Yesterday") {
          const yesterday = new Date(today);
          yesterday.setDate(today.getDate() - 1);
          if (itemDate < yesterday || itemDate >= today) return false;
        } else if (dayFilter === "Last 7 Days") {
          const weekAgo = new Date(today);
          weekAgo.setDate(today.getDate() - 7);
          if (itemDate < weekAgo) return false;
        }
      }

      // Type Filter
      if (!typeFilters.includes("all")) {
        // If a specific filter is active, we hide folders and search globally
        if (item.type === "folder") return false;

        const fileName = (item.name || "").toLowerCase();
        const fileExt = (item.fileExtension || fileName.split(".").pop() || "").toLowerCase();
        const itemType = (item.fileType || "").toLowerCase();

        const isDoc = /\.(pdf|doc|docx|txt|rtf|odt|xls|xlsx|ppt|pptx)$/.test(fileName) || 
                      itemType.includes("application/") ||
                      itemType.includes("document") ||
                      itemType.includes("text") ||
                      ["pdf", "doc", "docx", "txt", "rtf", "odt", "xls", "xlsx", "ppt", "pptx"].includes(fileExt);
        
        const isVideo = /\.(mp4|mkv|avi|mov|wmv|flv)$/.test(fileName) || 
                        itemType.includes("video") || 
                        itemType.includes("youtube") ||
                        fileExt === "youtube" ||
                        fileName.includes("youtube.com") || 
                        fileName.includes("youtu.be");

        const isUrl = (itemType === "webpage" || fileExt === "webpage" || fileName.startsWith("http"));

        let matches = false;
        if (typeFilters.includes("document") && isDoc) matches = true;
        if (typeFilters.includes("video") && isVideo) matches = true;
        if (typeFilters.includes("url") && isUrl) matches = true;

        if (!matches) return false;
      } else {
        // Only apply folder restriction if no type filters are active
        if (item.parentId !== currentFolder) return false;
      }

      return true;
    });

    // Apply manual Time Sort
    result.sort((a, b) => {
      if (appliedFolderSort === "Top") {
        if (a.type === "folder" && b.type !== "folder") return -1;
        if (a.type !== "folder" && b.type === "folder") return 1;
      }
      const dateA = new Date(a.updatedAt || a.createdAt).getTime();
      const dateB = new Date(b.updatedAt || b.createdAt).getTime();
      if (appliedSortOrder === "Ascending") {
        return dateA - dateB;
      } else {
        return dateB - dateA;
      }
    });

    return result;
  }, [
    items,
    currentFolder,
    searchTerm,
    repoView,
    role,
    dayFilter,
    typeFilters,
    appliedSortOrder,
    appliedFolderSort,
  ]);

  const { sortedData, requestSort, sortConfig } = useTableSort(filteredItems, {
    foldersFirst: appliedFolderSort === "Top",
  });

  const paginatedData = useMemo(() => {
    if (viewMode === "grid") return sortedData;
    const startIndex = (currentPage - 1) * entriesPerPage;
    return sortedData.slice(startIndex, startIndex + entriesPerPage);
  }, [sortedData, currentPage, entriesPerPage, viewMode]);

  const totalPages = Math.ceil(sortedData.length / entriesPerPage);

  // --- Fetch Data ---
  const fetchItems = useCallback(async () => {
    // Cancel previous request if still in progress
    if (fetchAbortControllerRef.current) {
      fetchAbortControllerRef.current.abort();
    }

    const controller = new AbortController();
    fetchAbortControllerRef.current = controller;
    setIsLoading(true);

    try {
      // Fetch personal space if active
      let personalSpaceRes = { data: { data: { folders: [], files: [] } } };
      if (repoView === "personal_space") {
        const endpoint = statusFilter === "Saved" ? "/files/get" : "/files/inactive-files";
        const params = {
          page: 1,
          limit: 1000, // Fetch more to allow client-side filtering/sorting consistent with other tabs
          search: searchTerm,
          dayFilter: dayFilter === "All" ? "" : dayFilter,
          sortOrder: appliedSortOrder === "Descending" ? "desc" : "asc",
          itemType: "all",
        };
        personalSpaceRes = await api.get(endpoint, {
          params,
          signal: controller.signal,
        });
      }

      // Fetch both endpoints in parallel for better performance
      const [trainedRes, docsRes] = await Promise.all([
        api.get("/trained-documents/get-trained-docs", {
          signal: controller.signal,
        }),
        role === "user" || role === "admin"
          ? api.get("/uploaded-files/global-user-docs", {
              signal: controller.signal,
            })
          : Promise.resolve({ data: { data: [] } }),
      ]);

      // If this request was aborted during the fetch, stop processing
      if (controller.signal.aborted) return;

      // Build organized files set for quick lookup
      const organizedFilesSet = new Set(
        trainedRes.data
          .filter((item) => item.itemType === "file" && item.uploaded_file_id)
          .map((f) => String(f.uploaded_file_id)),
      );

      // Get all docs and filter out already organized ones
      const allUserDocs = docsRes.data?.data || docsRes.data || [];
      const unorganizedFiles = allUserDocs.filter(
        (doc) =>
          !organizedFilesSet.has(String(doc.id || doc.file_id)) &&
          !organizedFilesSet.has(String(doc.trained_document_id)),
      );

      // Transform trained items (folders + organized files)
      const trainedItems = trainedRes.data.map((item) => ({
        id: String(item.id),
        name: item.name,
        type: item.itemType === "folder" ? "folder" : "file",
        parentId: item.parentId ? String(item.parentId) : null,
        size: item.size,
        updatedAt: item.updatedAt,
        uploadedBy: item.uploadedBy,
        uploaded_file_id: item.uploaded_file_id,
        pythonFileUuid: item.pythonFileUuid,
        mode: item.mode,
        ownerType: item.ownerType || "user",
        isUnorganized: false,
        approvalStatus: item.status || "approved",
        fileStatus: item.fileStatus || "1",
        createdAt: item.createdAt || item.updatedAt,
        fileType: item.type,
        fileExtension: item.file_extension,
      }));

      // Transform unorganized files
      const unorganizedItems = unorganizedFiles.map((doc) => ({
        id: `unorganized-${doc.id || doc.file_id}`,
        name: doc.name || doc.original_filename,
        type: "file",
        parentId: doc.folder_id ? String(doc.folder_id) : null,
        size: doc.size || doc.file_size,
        updatedAt: doc.updatedAt || doc.created_timestamp,
        uploadedBy: doc.uploaded_by || "Me",
        uploaded_file_id: doc.id || doc.file_id,
        pythonFileUuid: doc.python_file_uuid,
        mode: doc.mode || doc.upload_mode,
        ownerType:
          doc.mode === "global" ||
          doc.mode === "group" ||
          doc.upload_mode === "global" ||
          doc.upload_mode === "group"
            ? "admin"
            : "user",
        isUnorganized: true,
        approvalStatus: doc.approval_status || "approved",
        fileStatus: doc.file_status || "1",
        createdAt: doc.createdAt,
        fileType: doc.type,
        fileExtension: doc.file_extension,
      }));

      // Combine and deduplicate by using a Map
      const itemsMap = new Map();
      trainedItems.forEach((item) => itemsMap.set(item.id, item));
      unorganizedItems.forEach((item) => {
        if (!itemsMap.has(item.id)) itemsMap.set(item.id, item);
      });

      // Transform personal space items
      const personalSpaceData = personalSpaceRes.data?.data || { folders: [], files: [] };
      const psFolders = personalSpaceData.folders || [];
      const psFiles = personalSpaceData.files || [];
        
      psFolders.forEach((folder) => {
        const item = {
          id: `ps-folder-${folder.user_files_id}`,
          name: folder.name || folder.user_files_name,
          type: "folder",
          parentId: folder.parent_id ? `ps-folder-${folder.parent_id}` : null,
          size: 0,
          updatedAt: folder.updated_timestamp || folder.created_timestamp,
          uploadedBy: "Me",
          isPersonalSpace: true,
          isUnorganized: false,
          approvalStatus: "approved",
          fileStatus: folder.user_files_status || "1",
          createdAt: folder.created_timestamp,
          fileType: "folder",
          fileExtension: "folder",
          originalId: folder.user_files_id,
        };
        itemsMap.set(item.id, item);
      });

      psFiles.forEach((doc) => {
        const item = {
          id: `ps-file-${doc.user_files_id}`,
          name: doc.name || doc.user_files_name,
          type: "file",
          parentId: doc.parent_id ? `ps-folder-${doc.parent_id}` : null,
          size: doc.file_size,
          updatedAt: doc.updated_timestamp || doc.created_timestamp,
          uploadedBy: "Me",
          isPersonalSpace: true,
          isUnorganized: false,
          approvalStatus: "approved",
          fileStatus: doc.user_files_status || "1",
          createdAt: doc.created_timestamp,
          fileType: doc.file_type || doc.file_extension,
          fileExtension: doc.file_extension,
          originalId: doc.user_files_id,
        };
        itemsMap.set(item.id, item);
      });

      setItems(Array.from(itemsMap.values()));
    } catch (err) {
      if (
        err.name === "AbortError" ||
        err.name === "CanceledError" ||
        err.code === "ERR_CANCELED" ||
        controller.signal.aborted
      ) {
        console.log("Fetch cancelled");
        return;
      }
      console.error("Fetch items error:", err);
      showErrorToast("Failed to load repository items");
    } finally {
      // Only set loading to false if this request is still the active one
      if (fetchAbortControllerRef.current === controller) {
        setIsLoading(false);
      }
    }
  }, [role, repoView, statusFilter, searchTerm, dayFilter, appliedSortOrder]);

  useEffect(() => {
    fetchItems();
    return () => {
      if (fetchAbortControllerRef.current) {
        fetchAbortControllerRef.current.abort();
      }
    };
  }, [fetchItems]);

  // --- Handlers ---
  const handleTypeFilterChange = (filter) => {
    setTypeFilters((prev) => {
      if (filter === "all") return ["all"];
      const next = prev.filter((f) => f !== "all");
      if (next.includes(filter)) {
        const updated = next.filter((f) => f !== filter);
        return updated.length === 0 ? ["all"] : updated;
      }
      return [...next, filter];
    });
    setCurrentPage(1);
  };

  const handleNavigate = (folderId, folderName) => {
    const safeFolderId = folderId ? String(folderId) : null;
    setCurrentFolder(safeFolderId);
    setSelectedItems(new Set());
    if (safeFolderId === null) {
      setBreadcrumbs([{ id: null, name: "Root" }]);
    } else {
      setBreadcrumbs((prev) => {
        const index = prev.findIndex((b) => b.id === safeFolderId);
        if (index !== -1) return prev.slice(0, index + 1);
        return [...prev, { id: safeFolderId, name: folderName }];
      });
    }
    setSearchTerm("");
  };

  const replaceFolderIdInState = (tempId, realId) => {
    const nextId = String(realId);

    setItems((prev) =>
      prev.map((item) => ({
        ...item,
        id: item.id === tempId ? nextId : item.id,
        parentId: item.parentId === tempId ? nextId : item.parentId,
      })),
    );
    setCurrentFolder((prev) => (prev === tempId ? nextId : prev));
    setBreadcrumbs((prev) =>
      prev.map((crumb) => ({
        ...crumb,
        id: crumb.id === tempId ? nextId : crumb.id,
      })),
    );
    setMoveModal((prev) => ({
      ...prev,
      targetFolderId: prev.targetFolderId === tempId ? nextId : prev.targetFolderId,
      items: prev.items.map((item) => ({
        ...item,
        id: item.id === tempId ? nextId : item.id,
        parentId: item.parentId === tempId ? nextId : item.parentId,
      })),
    }));
    setDeleteModal((prev) => ({
      ...prev,
      items: prev.items.map((item) => ({
        ...item,
        id: item.id === tempId ? nextId : item.id,
        parentId: item.parentId === tempId ? nextId : item.parentId,
      })),
    }));
    setSelectedItems((prev) => {
      if (!prev.has(tempId)) return prev;
      const next = new Set(prev);
      next.delete(tempId);
      next.add(nextId);
      return next;
    });
  };

  const handleCreateFolder = async () => {
    try {
      const isPS = repoView === "personal_space";
      const newFolderId = `temp-${Date.now()}`;
      const optimisticFolder = {
        id: isPS ? `ps-folder-${newFolderId}` : newFolderId,
        name: createFolderModal.name.trim(),
        type: "folder",
        parentId: currentFolder,
        size: 0,
        updatedAt: new Date().toISOString(),
        uploadedBy: "Me",
        isPersonalSpace: isPS,
        isUnorganized: false,
        approvalStatus: "approved",
        fileStatus: "1",
        createdAt: new Date().toISOString(),
      };

      // Optimistically add folder to state
      setItems((prev) => [...prev, optimisticFolder]);
      setCreateFolderModal({ show: false, name: "" });
      showSuccessToast("Folder created");

      // Make API call
      if (isPS) {
        const res = await api.post("/files/create-folder", {
          folder_name: createFolderModal.name.trim(),
          parent_id: currentFolder ? currentFolder.replace("ps-folder-", "") : null,
        });
        const createdFolderId = res.data?.folder?.user_files_id;
        if (createdFolderId) {
          replaceFolderIdInState(optimisticFolder.id, `ps-folder-${createdFolderId}`);
        }
      } else {
        const res = await api.post("/trained-documents/create-folder", {
          name: createFolderModal.name.trim(),
          parent_id: currentFolder,
        });
        const createdFolderId = res.data?.data?.id;
        if (createdFolderId) {
          replaceFolderIdInState(newFolderId, createdFolderId);
        }
      }
    } catch (err) {
      showErrorToast("Failed to create folder");
      // Revert optimistic update on error
      fetchItems();
    }
  };

  const handleDelete = async (strategy = "DELETE") => {
    const itemsToDelete = deleteModal.items;
    const organizedIds = itemsToDelete
      .filter((i) => !i.id.toString().startsWith("unorganized-"))
      .map((i) => i.id);
    const unorganizedIds = itemsToDelete
      .filter((i) => i.id.toString().startsWith("unorganized-"))
      .map((i) => i.id.replace("unorganized-", ""));

    try {
      if (deleteModal.items.length === 1 && deleteModal.items[0].isPersonalSpace) {
        const item = deleteModal.items[0];
        if (item.fileStatus === "1") {
          // Soft delete
          await api.put(`/files/mark-inactive/${item.originalId}`);
          showSuccessToast("Moved to Deleted");
        } else {
          // Permanent delete
          await api.delete(`/files/delete/${item.originalId}`);
          showSuccessToast("Permanently deleted");
        }
        setDeleteModal({ show: false, items: [] });
        fetchItems();
        return;
      }

      // Optimistically remove items from state
      const deletedIds = new Set(itemsToDelete.map((i) => i.id));
      setItems((prev) => prev.filter((item) => !deletedIds.has(item.id)));
      setDeleteModal({ show: false, items: [] });
      setSelectedItems(new Set());

      showSuccessToast(
        itemsToDelete.length > 1
          ? `Deleted ${itemsToDelete.length} items`
          : "Item deleted",
      );

      // Execute delete operations
      await Promise.all([
        organizedIds.length > 0
          ? api.delete(`/trained-documents/${organizedIds[0]}`, {
              data: { item_ids: organizedIds, delete_strategy: strategy },
            })
          : Promise.resolve(),
        ...unorganizedIds.map((fileId) =>
          api.delete(`/uploaded-files/docs/${fileId}`),
        ),
      ]);
    } catch (err) {
      console.error("Delete error:", err);
      showErrorToast("Delete failed");
      // Refetch on error to restore state
      fetchItems();
    }
  };

  const handleDownload = async (item) => {
    if (item.type === "folder") return;
    try {
      if (item.isPersonalSpace) {
        const res = await api.get(`/files/download`, {
          params: { user_files_id: item.originalId },
          responseType: "blob",
        });
        const url = window.URL.createObjectURL(new Blob([res.data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", item.name);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
        return;
      }
      const idToUse = item.uploaded_file_id || item.id;
      const res = await api.get(`/trained-documents/download/${idToUse}`, {
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", item.name);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Download failed:", err);
      if (err.response && err.response.data instanceof Blob) {
        const reader = new FileReader();
        reader.onload = () => {
          try {
            const errorData = JSON.parse(reader.result);
            showErrorToast(errorData.message || "Download failed");
          } catch (e) {
            showErrorToast("Download failed");
          }
        };
        reader.readAsText(err.response.data);
      } else {
        showErrorToast(err.response?.data?.message || "Download failed");
      }
    }
  };

  const handleBulkDelete = async (selectedIds) => {
    const itemsToDelete = items.filter((i) => selectedIds.has(i.id));
    const organizedIds = itemsToDelete
      .filter((i) => !i.isUnorganized)
      .map((i) => i.id);
    const unorganizedIds = itemsToDelete
      .filter((i) => i.isUnorganized)
      .map((i) => i.id.replace("unorganized-", ""));

    try {
      // Optimistically remove items from state
      setItems((prev) =>
        prev.filter((item) => !selectedIds.has(item.id)),
      );
      setSelectedItems(new Set());

      showSuccessToast(`Deleted ${itemsToDelete.length} items`);

      // Execute delete operations in parallel
      await Promise.all([
        organizedIds.length > 0
          ? api.delete(`/trained-documents/${organizedIds[0]}`, {
              data: { item_ids: organizedIds, delete_strategy: "DELETE" },
            })
          : Promise.resolve(),
        ...unorganizedIds.map((fileId) =>
          api.delete(`/uploaded-files/docs/${fileId}`),
        ),
      ]);
    } catch (err) {
      console.error("Bulk delete error:", err);
      showErrorToast("Bulk delete failed");
      // Refetch on error to restore state
      fetchItems();
    }
  };

  const handleMove = async (itemsToMove, targetFolderId) => {
    if (repoView === "personal_space") {
      try {
        const targetParentId = targetFolderId ? targetFolderId.replace("ps-folder-", "") : null;
        const itemIds = itemsToMove.map(item => item.originalId);

        // Optimistically update items' parentId in state
        const movedItemIds = new Set(itemsToMove.map((i) => i.id));
        setItems((prev) =>
          prev.map((item) =>
            movedItemIds.has(item.id)
              ? { ...item, parentId: targetFolderId }
              : item,
          ),
        );

        setSelectedItems(new Set());
        showSuccessToast(`Moved ${itemsToMove.length} items`);

        await api.put("/files/bulk-move", {
          item_ids: itemIds,
          target_parent_id: targetParentId,
        });
      } catch (err) {
        console.error("Move error:", err);
        showErrorToast(err?.response?.data?.message || "Move failed");
        fetchItems();
      }
      return;
    }

    const unorganizedIds = itemsToMove
      .filter((i) => i.isUnorganized)
      .map((i) => i.id.replace("unorganized-", ""));
    const organizedItems = itemsToMove
      .filter((i) => !i.isUnorganized)
      .map((i) => ({ type: i.type, id: i.id }));

    try {
      // Optimistically update items' parentId in state
      const movedItemIds = new Set(itemsToMove.map((i) => i.id));
      setItems((prev) =>
        prev.map((item) =>
          movedItemIds.has(item.id)
            ? { ...item, parentId: targetFolderId }
            : item,
        ),
      );

      setSelectedItems(new Set());
      showSuccessToast(`Moved ${itemsToMove.length} items`);

      // Execute both move operations in parallel
      await Promise.all([
        unorganizedIds.length > 0
          ? api.put("/trained-documents/move-uploaded-files", {
              uploaded_file_ids: unorganizedIds,
              target_folder_id: targetFolderId,
            })
          : Promise.resolve(),
        organizedItems.length > 0
          ? api.put("/trained-documents/move-items", {
              items: organizedItems,
              target_folder_id: targetFolderId,
            })
          : Promise.resolve(),
      ]);
    } catch (err) {
      console.error("Move error:", err);
      showErrorToast(
        err?.response?.data?.error ||
          err?.response?.data?.message ||
          "Move failed",
      );
      // Refetch on error to restore state
      fetchItems();
    }
  };

  const toggleSelect = (id) => {
    setSelectedItems((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleChangeFileStatus = async (item) => {
    if (item.isPersonalSpace) {
      try {
        if (item.fileStatus === "0") {
          await api.put(`/files/revert/${item.originalId}`);
          showSuccessToast("Restored to Saved");
          fetchItems();
        }
      } catch (err) {
        console.error("Error restoring file:", err);
        showErrorToast(err?.response?.data?.message || "Something went wrong");
        fetchItems();
      }
      return;
    }

    if (!item.pythonFileUuid) {
      showErrorToast("File identifier missing");
      return;
    }
    try {
      // Optimistically toggle file status
      const newStatus = item.fileStatus === "1" ? "0" : "1";
      setItems((prev) =>
        prev.map((i) =>
          i.id === item.id ? { ...i, fileStatus: newStatus } : i,
        ),
      );

      await api.patch(
        `/uploaded-files/update_file_status/${item.pythonFileUuid}`,
      );
      showSuccessToast("Status updated successfully");
    } catch (err) {
      console.error("Error updating file status:", err);
      showErrorToast(err?.response?.data?.message || "Something went wrong");
      // Refetch on error
      fetchItems();
    }
  };

  const getApprovalFileId = (item) => {
    if (item.uploaded_file_id) return item.uploaded_file_id;
    if (typeof item.id === "string" && item.id.startsWith("unorganized-")) {
      return item.id.replace("unorganized-", "");
    }
    return item.id;
  };

  const handleChangeApprovalStatus = async (itemId, newStatus) => {
    const approvalFileId = getApprovalFileId(items.find((item) => item.id === itemId) || { id: itemId });
    
    try {
      // Optimistically update state
      setItems((prev) =>
        prev.map((item) =>
          item.id === itemId ? { ...item, approvalStatus: newStatus } : item,
        ),
      );

      await api.post(`/uploaded-files/approve-or-reject`, {
        file_id: approvalFileId,
        approval_status: newStatus,
        notes: "",
      });
      
      showSuccessToast(`Doc status set to ${newStatus}`);
    } catch (err) {
      console.error("Error updating approval status:", err);
      showErrorToast(err?.response?.data?.message || "Something went wrong");
      // Refetch on error
      fetchItems();
    }
  };

  const handleBulkApprovalStatus = async (newStatus) => {
    const fileItems = items.filter(
      (i) => selectedItems.has(i.id) && i.type === "file",
    );
    if (fileItems.length === 0) {
      showErrorToast("No files selected");
      return;
    }
    try {
      // Optimistically update state
      const updateIds = new Set(fileItems.map((i) => i.id));
      setItems((prev) =>
        prev.map((item) =>
          updateIds.has(item.id)
            ? { ...item, approvalStatus: newStatus }
            : item,
        ),
      );
      setSelectedItems(new Set());

      showSuccessToast(`${fileItems.length} file(s) set to ${newStatus}`);

      // Execute all approval updates in parallel
      await Promise.all(
        fileItems.map((f) =>
          api.post(`/uploaded-files/approve-or-reject`, {
            file_id: getApprovalFileId(f),
            approval_status: newStatus,
            notes: "",
          }),
        ),
      );
    } catch (err) {
      console.error("Bulk approval error:", err);
      showErrorToast("Failed to update one or more files");
      // Refetch on error
      fetchItems();
    }
  };

  // --- Render Helpers ---
  const getFileIcon = (item, size = 20) => {
    const name = item.name || "";
    const ext = (item.fileExtension || name.split(".").pop() || "").toLowerCase();
    const type = (item.fileType || "").toLowerCase();

    if (["jpg", "jpeg", "png", "gif", "svg"].includes(ext))
      return <ImageIcon size={size} className="repo-file-icon" />;

    const isVideo = /\.(mp4|mkv|avi|mov|wmv|flv)$/.test(name) || 
                    type.includes("video") || 
                    type.includes("youtube") ||
                    ext === "youtube" ||
                    name.includes("youtube.com") || 
                    name.includes("youtu.be");

    if (isVideo) {
      if (type.includes("youtube") || ext === "youtube" || name.includes("youtube.com") || name.includes("youtu.be")) {
        return <Youtube size={size} className="repo-file-icon" style={{ color: '#FF0000' }} />;
      }
      return <Video size={size} className="repo-file-icon" />;
    }

    if (type === "webpage" || ext === "webpage" || name.startsWith("http"))
      return <Globe size={size} className="repo-file-icon" />;

    if (["pdf", "doc", "docx", "txt", "rtf", "odt", "xls", "xlsx", "ppt", "pptx"].includes(ext) || type.includes("application/"))
      return <FileText size={size} className="repo-file-icon" />;
    
    if (["js", "jsx", "html", "css", "json"].includes(ext))
      return <FileCode size={size} className="repo-file-icon" />;
    
    return <FileIcon size={size} className="repo-file-icon" />;
  };

  const handleBulkDownload = async (selectedIds) => {
    const selectedList = items.filter((i) => selectedIds.has(i.id));
    const foldersSelected = selectedList.filter((i) => i.type === "folder");
    const filesToDownload = selectedList.filter((i) => i.type === "file");

    if (foldersSelected.length > 0 && filesToDownload.length > 0) {
      showErrorToast(
        "Folders cannot be downloaded. Only files will be processed.",
      );
    } else if (foldersSelected.length > 0) {
      showErrorToast("Cannot download folders.");
      return;
    }

    if (filesToDownload.length === 0) {
      showErrorToast("No files selected for download");
      return;
    }

    showSuccessToast(`Starting download of ${filesToDownload.length} files...`);

    for (const file of filesToDownload) {
      await handleDownload(file);
      await new Promise((resolve) => setTimeout(resolve, 500));
    }
  };

  const selectedItemDetails = items.filter((i) => selectedItems.has(i.id));
  const selectedDraggableItems = selectedItemDetails.filter((item) =>
    canModifyRepoItem(item, role),
  );
  const hasSelectedFolder = selectedItemDetails.some((i) => i.type === "folder");
  const selectedFileCount = selectedItemDetails.filter((i) => i.type === "file").length;
  const canModifySelectedItems = selectedItemDetails.some(
    (item) => canModifyRepoItem(item, role),
  );

  const handleAction = (action, item) => {
    switch (action) {
      case "download":
        handleDownload(item);
        break;
      case "status":
        handleChangeFileStatus(item);
        break;
      case "process":
        handleProcessFile(item);
        break;
      case "revert":
        handleChangeFileStatus(item);
        break;
      case "delete":
        setDeleteModal({ show: true, items: [item] });
        break;
      case "move":
        setMoveModal({ show: true, items: [item], targetFolderId: null });
        break;
      default:
        break;
    }
  };

  const handleProcessFile = async (file) => {
    setSelectedFileToTrain(file);
    setShowFirstModal(true);
  };

  const [selectedFileToTrain, setSelectedFileToTrain] = useState(null);
  const [showTrainingModal, setShowTrainingModal] = useState(false);
  const [isTraining, setIsTraining] = useState(false);

  const handleSendForTraining = async (file, mode) => {
    try {
      setIsTraining(true);
      setShowTrainingModal(false);
      showSuccessToast(`Sending ${file.name} for training...`);

      // Fetch file blob from personal space
      const downloadRes = await api.get(`/files/download`, {
        params: { user_files_id: file.originalId },
        responseType: "blob",
      });
      
      const blob = downloadRes.data;
      const selectedFile = new File([blob], file.name, {
        type: file.fileType || blob.type || "application/octet-stream",
      });

      const formData = new FormData();
      formData.append("files", selectedFile);
      formData.append("mode", mode.toLowerCase());
      formData.append("model", "llama-3.3-70b-versatile");

      const response = await api.post("/uploaded-files/newQueryUpload", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (response.status === 200 || response.status === 207) {
        showSuccessToast(`${file.name} trained successfully!`);
        // Mark as inactive after training
        await api.put(`/files/mark-inactive/${file.originalId}`);
        fetchItems();
      }
    } catch (err) {
      console.error("Training failed:", err);
      if (err.response && err.response.data instanceof Blob) {
        const reader = new FileReader();
        reader.onload = () => {
          try {
            const errorData = JSON.parse(reader.result);
            showErrorToast(errorData.message || "Training failed");
          } catch (e) {
            showErrorToast("Training failed");
          }
        };
        reader.readAsText(err.response.data);
      } else {
        showErrorToast(err.response?.data?.message || "Training failed");
      }
    } finally {
      setIsTraining(false);
      setSelectedFileToTrain(null);
    }
  };

  const uploadToBackend = async () => {
    try {
      setIsSubmitting(true);
      const formData = new FormData();
      
      // Since we are training an existing file, we might need a different approach
      // but following SaveDocsFiles.js logic for summary submission:
      
      const mode = uploadMode === "Global" ? "pending_global" : uploadMode.toLowerCase();
      formData.append("upload_mode", mode);
      formData.append("file_description", description);
      formData.append("file_data_name", dataName);

      summaryDataList.forEach((item) =>
        formData.append(
          "file_summaries",
          JSON.stringify({
            filename: item.filename,
            summary: item.summary,
            short_description: item.short_description,
          }),
        ),
      );

      // In RepoOrganizer, we might need to send the original file again or a reference.
      // But handleSendForTraining already did the heavy lifting.
      // Actually, SaveDocsFiles.js's uploadToBackend is for NEW uploads.
      // For existing files, it seems handleSendForTraining does the job.
      
      // Let's assume for now we just mark it inactive after summary is reviewed.
      const firstItem = summaryDataList[0];
      if (firstItem && firstItem.originalId) {
        await api.put(`/files/mark-inactive/${firstItem.originalId}`);
      }

      setShowSummaryModal(false);
      setSummaryDataList([]);
      fetchItems();
      showSuccessToast("Training completed!");
    } catch (err) {
      console.error("Submission failed:", err);
      showErrorToast("Submission failed!");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleFileChange = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;

    try {
      setIsLoading(true);
      const formData = new FormData();
      files.forEach((file) => formData.append("files", file));

      const res = await api.post("/files/upload-multiple", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      showSuccessToast("Files uploaded successfully");
      fetchItems();
    } catch (error) {
      console.error("Upload failed:", error);
      showErrorToast("Upload failed");
    } finally {
      setIsLoading(false);
      e.target.value = "";
    }
  };

  return (
    <DndProvider backend={HTML5Backend}>
      <RepoDragPreview />
      <div
        ref={containerRef}
        className="repo-container"
        onMouseDown={handleMouseDown}
      >
        <div className="repo-glass-panel repo-animate-fade">
          <div className="repo-tabs">
            <button
              className={`repo-tab ${repoView === "personal" ? "active" : ""}`}
              onClick={() => {
                setRepoView("personal");
                setCurrentFolder(null);
                setBreadcrumbs([{ id: null, name: "Root" }]);
                setSelectedItems(new Set());
              }}
            >
              Personal Repo
            </button>
            <button
              className={`repo-tab ${repoView === "global" ? "active" : ""}`}
              onClick={() => {
                setRepoView("global");
                setCurrentFolder(null);
                setBreadcrumbs([{ id: null, name: "Root" }]);
                setSelectedItems(new Set());
              }}
            >
              Global Repo
            </button>
            <button
              className={`repo-tab ${repoView === "personal_space" ? "active" : ""}`}
              onClick={() => {
                setRepoView("personal_space");
                setCurrentFolder(null);
                setBreadcrumbs([{ id: null, name: "Root" }]);
                setSelectedItems(new Set());
              }}
            >
              My personal space
            </button>
          </div>
          <div className="repo-header">
            <div className="repo-breadcrumbs">
              {breadcrumbs.length <= 4 ? (
                breadcrumbs.map((b, i) => (
                  <React.Fragment key={b.id || "root"}>
                    <span
                      className="repo-breadcrumb-item"
                      onClick={() => handleNavigate(b.id, b.name)}
                      title={b.name}
                    >
                      {i === 0 ? <Folder size={18} /> : null}
                      <span className="repo-breadcrumb-text">{b.name}</span>
                    </span>
                    {i < breadcrumbs.length - 1 && (
                      <ChevronRight
                        size={14}
                        className="repo-breadcrumb-separator"
                      />
                    )}
                  </React.Fragment>
                ))
              ) : ( 
                <>
                  <React.Fragment key={breadcrumbs[0].id || "root"}>
                    <span
                      className="repo-breadcrumb-item"
                      onClick={() => handleNavigate(breadcrumbs[0].id, breadcrumbs[0].name)}
                      title={breadcrumbs[0].name}
                    >
                      <Folder size={18} />
                      <span className="repo-breadcrumb-text">{breadcrumbs[0].name}</span>
                    </span>
                    <ChevronRight size={14} className="repo-breadcrumb-separator" />
                  </React.Fragment>

                  <div className="dropdown border-0 d-flex align-items-center">
                    <span
                      className="repo-breadcrumb-item repo-breadcrumb-ellipsis"
                      data-bs-toggle="dropdown"
                      title="Hidden folders"
                    >
                      ...
                    </span>
                    <ul className="dropdown-menu">
                      {breadcrumbs.slice(1, -2).map((b) => (
                        <li key={b.id}>
                          <button
                            className="dropdown-item"
                            onClick={() => handleNavigate(b.id, b.name)}
                          >
                            <Folder size={14} style={{ marginRight: '8px' }} />
                            {b.name}
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <ChevronRight size={14} className="repo-breadcrumb-separator" />

                  {breadcrumbs.slice(-2).map((b, i) => (
                    <React.Fragment key={b.id}>
                      <span
                        className="repo-breadcrumb-item"
                        onClick={() => handleNavigate(b.id, b.name)}
                        title={b.name}
                      >
                        <span className="repo-breadcrumb-text">{b.name}</span>
                      </span>
                      {i < 1 && (
                        <ChevronRight size={14} className="repo-breadcrumb-separator" />
                      )}
                    </React.Fragment>
                  ))}
                </>
              )}
            </div>

            <div className="repo-actions">
              <input
                type="file"
                ref={fileInputRef}
                style={{ display: "none" }}
                multiple
                onChange={handleFileChange}
              />
             
              {repoView !== "personal_space" && (
                <div className="repo-type-filters">
                  <button
                    className={`repo-type-chip ${typeFilters.includes("all") ? "active" : ""}`}
                    onClick={() => handleTypeFilterChange("all")}
                  >
                    All
                  </button>
                  <button
                    className={`repo-type-chip ${typeFilters.includes("document") ? "active" : ""}`}
                    onClick={() => handleTypeFilterChange("document")}
                  >
                    Document
                  </button>
                  <button
                    className={`repo-type-chip ${typeFilters.includes("video") ? "active" : ""}`}
                    onClick={() => handleTypeFilterChange("video")}
                  >
                    Videos
                  </button>
                  <button
                    className={`repo-type-chip ${typeFilters.includes("url") ? "active" : ""}`}
                    onClick={() => handleTypeFilterChange("url")}
                  >
                    URLs
                  </button>
                </div>
              )}

              {repoView === "personal_space" && (
                <div className="repo-type-filters ms-3">
                  <button
                    className={`repo-type-chip ${statusFilter === "Saved" ? "active" : ""}`}
                    onClick={() => {
                      setStatusFilter("Saved");
                      setCurrentPage(1);
                    }}
                  >
                    Saved
                  </button>
                  <button
                    className={`repo-type-chip ${statusFilter === "Deleted" ? "active" : ""}`}
                    onClick={() => {
                      setStatusFilter("Deleted");
                      setCurrentPage(1);
                    }}
                  >
                    Deleted
                  </button>
                </div>
              )}

              {!(repoView === "global" && role !== "admin") && (
                <button
                  className="repo-btn repo-btn-primary"
                  onClick={() => setCreateFolderModal({ show: true, name: "" })}
                  title="New Folder"
                >
                  <Plus size={18} />
                </button>
              )}
              {repoView === "personal_space" && (
                <button
                  className="repo-btn repo-btn-primary"
                  onClick={() => fileInputRef.current?.click()}
                  title="Upload Files"
                >
                  <Upload size={18} />
                </button>
              )}

              <div className="repo-view-toggle">
                <button
                  className={`repo-view-btn ${viewMode === "grid" ? "active" : ""}`}
                  onClick={() => {
                    setViewMode("grid");
                    setSelectedItems(new Set());
                  }}
                >
                  <Grid size={18} />
                </button>
                <button
                  className={`repo-view-btn ${viewMode === "list" ? "active" : ""}`}
                  onClick={() => {
                    setViewMode("list");
                    setSelectedItems(new Set());
                  }}
                >
                  <List size={18} />
                </button>
              </div>
            </div>
          </div>
          <div className="repo-filters-bar">
            <div className="repo-filters-left">
              <div className="repo-entries-select">
                <select
                  className="repo-select"
                  value={entriesPerPage}
                  onChange={(e) => {
                    setEntriesPerPage(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                >
                  {[10, 25, 50, 100].map((n) => (
                    <option key={n} value={n}>
                      {n}
                    </option>
                  ))}
                </select>
                <span>Entries per page</span>
              </div>
            </div>
            <div className="repo-filters-right">
              <div
                className="repo-search-container"
                style={{ position: "relative" }}
              >
                <Search
                  size={18}
                  style={{
                    position: "absolute",
                    left: "10px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    color: "var(--repo-text-muted)",
                  }}
                />
                <input
                  type="text"
                  placeholder="Search Documents"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="repo-search-input"
                />
              </div>

              <div
                className="repo-filter-dropdown-wrapper"
                ref={filterDropdownRef}
              >
                <button
                  className={`repo-filter-toggle-btn ${showFilterDropdown ? "active" : ""}`}
                  onClick={() => setShowFilterDropdown(!showFilterDropdown)}
                >
                  <Filter size={18} />
                </button>

                {showFilterDropdown && (
                  <div className="repo-filter-dropdown-menu">
                    <div className="repo-filter-section">
                      <div className="repo-filter-label">Time</div>
                      <div className="repo-radio-group">
                        <label className="repo-radio-label">
                          <input
                            type="radio"
                            name="time"
                            value="Ascending"
                            checked={tempSortOrder === "Ascending"}
                            onChange={() => setTempSortOrder("Ascending")}
                          />
                          <span className="repo-radio-custom"></span>
                          Ascending
                        </label>
                        <label className="repo-radio-label">
                          <input
                            type="radio"
                            name="time"
                            value="Descending"
                            checked={tempSortOrder === "Descending"}
                            onChange={() => setTempSortOrder("Descending")}
                          />
                          <span className="repo-radio-custom"></span>
                          Descending
                        </label>
                      </div>
                    </div>

                    <div className="repo-filter-divider"></div>

                    <div className="repo-filter-section">
                      <div className="repo-filter-label">Folders</div>
                      <div className="repo-radio-group">
                        <label className="repo-radio-label">
                          <input
                            type="radio"
                            name="folderSort"
                            value="Top"
                            checked={tempFolderSort === "Top"}
                            onChange={() => setTempFolderSort("Top")}
                          />
                          <span className="repo-radio-custom"></span>
                          Folders on Top
                        </label>
                        <label className="repo-radio-label">
                          <input
                            type="radio"
                            name="folderSort"
                            value="Mixed"
                            checked={tempFolderSort === "Mixed"}
                            onChange={() => setTempFolderSort("Mixed")}
                          />
                          <span className="repo-radio-custom"></span>
                          Mixed with Files
                        </label>
                      </div>
                    </div>

                    <div className="repo-filter-divider"></div>

                    <div className="repo-filter-section">
                      <div className="repo-filter-label">By Day</div>
                      <div className="repo-radio-group">
                        <label className="repo-radio-label">
                          <input
                            type="radio"
                            name="day"
                            value="Today"
                            checked={tempDayFilter === "Today"}
                            onChange={() => setTempDayFilter("Today")}
                          />
                          <span className="repo-radio-custom"></span>
                          Today
                        </label>
                        <label className="repo-radio-label">
                          <input
                            type="radio"
                            name="day"
                            value="Yesterday"
                            checked={tempDayFilter === "Yesterday"}
                            onChange={() => setTempDayFilter("Yesterday")}
                          />
                          <span className="repo-radio-custom"></span>
                          Yesterday
                        </label>
                        <label className="repo-radio-label">
                          <input
                            type="radio"
                            name="day"
                            value="All"
                            checked={tempDayFilter === "All"}
                            onChange={() => setTempDayFilter("All")}
                          />
                          <span className="repo-radio-custom"></span>
                          All
                        </label>
                      </div>
                    </div>

                    <div className="repo-filter-actions">
                      <button
                        className="repo-btn repo-btn-secondary"
                        onClick={handleResetFilter}
                      >
                        Reset
                      </button>
                      <button
                        className="repo-btn repo-btn-primary"
                        onClick={handleApplyFilter}
                      >
                        Apply Now
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
          {isLoading ? (
            <div className="repo-loader-container">
              <Loader2 className="animate-spin" size={32} />
              <p className="mt-2 ">Loading repository...</p>
            </div>
          ) : filteredItems.length === 0 ? (
            <div
              className="repo-dropzone"
              onDragOver={(e) => e.preventDefault()}
            >
              <Folder
                size={48}
                style={{ marginBottom: "1rem", opacity: 0.3 }}
              />
              <p>No items found in this directory</p>
              <p style={{ fontSize: "0.8rem", opacity: 0.6 }}>
                Try creating a folder or check other directories
              </p>
            </div>
          ) : viewMode === "grid" ? (
            <div className="repo-grid">
              {filteredItems.map((item) => (
                <DraggableItem
                  key={item.id}
                  item={item}
                  viewMode={viewMode}
                  onNavigate={handleNavigate}
                  onToggleSelect={toggleSelect}
                  onAction={handleAction}
                  isSelected={selectedItems.has(item.id)}
                  getFileIcon={getFileIcon}
                    onMove={handleMove}
                    role={role}
                    dragSelectionRef={dragSelectionRef}
                    selectedDragItems={selectedDraggableItems}
                    onApprovalStatusChange={
                      role === "admin" ? handleChangeApprovalStatus : undefined
                    }
                  />
                ))}
            </div>
          ) : (
            <table className="repo-list">
              <thead>
                <tr>
                  <th style={{ width: "40px" }}>
                    <input
                      type="checkbox"
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedItems(
                            new Set(filteredItems.map((i) => i.id)),
                          );
                        } else {
                          setSelectedItems(new Set());
                        }
                      }}
                      checked={
                        filteredItems.length > 0 &&
                        selectedItems.size === filteredItems.length
                      }
                    />
                  </th>
                  <SortableHeader
                    column="name"
                    label="Name"
                    sortConfig={sortConfig}
                    onSort={requestSort}
                    style={{ width: "30%" }}
                  />
                  <SortableHeader
                    column="type"
                    label="Type"
                    sortConfig={sortConfig}
                    onSort={requestSort}
                    style={{ width: "80px" }}
                  />
                  <SortableHeader
                    column="size"
                    label="Size"
                    sortConfig={sortConfig}
                    onSort={requestSort}
                    style={{ width: "110px" }}
                  />
                  <SortableHeader
                    column="approvalStatus"
                    label="Doc Status"
                    sortConfig={sortConfig}
                    onSort={requestSort}
                    style={{ width: "140px" }}
                  />
                  <SortableHeader
                    column="fileStatus"
                    label="Status"
                    sortConfig={sortConfig}
                    onSort={requestSort}
                    style={{ width: "100px" }}
                  />
                  <SortableHeader
                    column="createdAt"
                    label="Uploaded On"
                    sortConfig={sortConfig}
                    onSort={requestSort}
                    style={{ width: "120px" }}
                  />
                  <SortableHeader
                    column="updatedAt"
                    label="Updated"
                    sortConfig={sortConfig}
                    onSort={requestSort}
                    style={{ width: "120px" }}
                  />
                  {role === "admin" && (
                    <SortableHeader
                      column="uploadedBy"
                      label="Uploaded By"
                      sortConfig={sortConfig}
                      onSort={requestSort}
                      style={{ width: "150px" }}
                    />
                  )}
                  <th style={{ width: "110px" }}>Actions</th>
                </tr>
              </thead>

              <tbody>
                {sortedData.map((item) => (
                  <DraggableItem
                    key={item.id}
                    item={item}
                    viewMode={viewMode}
                    onNavigate={handleNavigate}
                    onToggleSelect={toggleSelect}
                    onAction={handleAction}
                    isSelected={selectedItems.has(item.id)}
                    getFileIcon={getFileIcon}
                    onMove={handleMove}
                    role={role}
                    dragSelectionRef={dragSelectionRef}
                    selectedDragItems={selectedDraggableItems}
                    onApprovalStatusChange={
                      role === "admin" ? handleChangeApprovalStatus : undefined
                    }
                  />
                ))}
              </tbody>
            </table>
          )}
          {viewMode === "list" && sortedData.length > entriesPerPage && (
            <div className="repo-pagination">
              <button
                className="repo-page-btn"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage((prev) => prev - 1)}
              >
                Previous
              </button>

              <div className="repo-page-numbers">
                {Array.from({ length: totalPages }, (_, i) => i + 1).map(
                  (num) => (
                    <button
                      key={num}
                      className={`repo-page-num ${currentPage === num ? "active" : ""}`}
                      onClick={() => setCurrentPage(num)}
                    >
                      {num}
                    </button>
                  ),
                )}
              </div>

              <button
                className="repo-page-btn"
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage((prev) => prev + 1)}
              >
                Next
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Floating Action Bar */}
      {selectedItems.size > 0 && (
        <div className="repo-floating-actions">
          <div className="repo-floating-left">
            <button
              className="repo-floating-close"
              onClick={() => setSelectedItems(new Set())}
              title="Clear selection"
            >
              <X size={18} />
            </button>
            <span className="repo-floating-count">
              {selectedItems.size} selected
            </span>
          </div>
          <div className="repo-floating-right">
            {!hasSelectedFolder && (
              <button
                className="repo-floating-btn"
                onClick={() => handleBulkDownload(selectedItems)}
                title="Download"
              >
                <Download size={20} />
              </button>
            )}
            {canModifySelectedItems && (
              <button
                className="repo-floating-btn"
                onClick={() =>
                  setMoveModal({
                    show: true,
                    items: items.filter((i) => selectedItems.has(i.id)),
                    targetFolderId: null,
                  })
                }
                title="Move"
              >
                <FolderTree size={20} />
              </button>
            )}
            {/* {canModifySelectedItems && (
              <button
                className="repo-floating-btn"
                onClick={() =>
                  setDeleteModal({
                    show: true,
                    items: items.filter((i) => selectedItems.has(i.id)),
                  })
                }
                title="Delete"
                style={{ color: "var(--danger-color)" }}
              >
                <Trash2 size={20} />
              </button>
            )} */}

            {role === "admin" && !hasSelectedFolder && selectedFileCount > 0 && (
              <>
                <div
                  style={{
                    width: "1px",
                    height: "24px",
                    background: "rgba(255,255,255,0.2)",
                    margin: "0 4px",
                  }}
                />
                <button
                  className="repo-floating-btn"
                  onClick={() => handleBulkApprovalStatus("approved")}
                  title="Approve"
                  style={{ color: "#4ade80" }}
                >
                  <CheckCircle size={20} />
                </button>
                <button
                  className="repo-floating-btn"
                  onClick={() => handleBulkApprovalStatus("pending")}
                  title="Set Pending"
                  style={{ color: "#fbbf24" }}
                >
                  <Clock size={20} />
                </button>
                <button
                  className="repo-floating-btn"
                  onClick={() => handleBulkApprovalStatus("rejected")}
                  title="Reject"
                  style={{ color: "#f87171" }}
                >
                  <XCircle size={20} />
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteModal.show && (
        <div
          className="repo-modal-overlay"
          onClick={() => setDeleteModal({ show: false, items: [] })}
        >
          <div
            className="repo-modal repo-animate-fade"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className="repo-modal-close"
              onClick={() => setDeleteModal({ show: false, items: [] })}
            >
              <X size={20} />
            </button>

            {deleteModal.items.length === 1 &&
            deleteModal.items[0].type === "folder" ? (
              <>
                <h3>Delete Folder</h3>
                <p>
                  What would you like to do with the files inside{" "}
                  <strong>{deleteModal.items[0].name}</strong>?
                </p>
                <div className="repo-modal-actions">
                  <button
                    className="repo-btn"
                    onClick={() => handleDelete("KEEP")}
                  >
                    Keep Files (Move to parent)
                  </button>
                  <button
                    className="repo-btn repo-btn-danger"
                    onClick={() => handleDelete("DELETE")}
                  >
                    Delete Everything
                  </button>
                </div>
              </>
            ) : (
              <>
                <h3>
                  Delete{" "}
                  {deleteModal.items.length > 1
                    ? `${deleteModal.items.length} Items`
                    : deleteModal.items[0]?.type === "folder"
                      ? "Folder"
                      : "File"}
                </h3>
                <p>
                  Are you sure you want to delete{" "}
                  <strong>
                    {deleteModal.items.length > 1
                      ? `${deleteModal.items.length} selected items`
                      : deleteModal.items[0]?.name}
                  </strong>
                  ?
                </p>
                <div
                  className="repo-modal-actions"
                  style={{
                    flexDirection: "row",
                    justifyContent: "space-between",
                  }}
                >
                  <button
                    className="repo-btn repo-btn-danger"
                    onClick={() => handleDelete("DELETE")}
                  >
                    Confirm Delete
                  </button>
                  <button
                    className="repo-btn"
                    onClick={() => setDeleteModal({ show: false, items: [] })}
                  >
                    Cancel
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Move Modal */}
      {moveModal.show && (
        <div
          className="repo-modal-overlay"
          onClick={() =>
            setMoveModal({ show: false, items: [], targetFolderId: null })
          }
        >
          <div
            className="repo-modal repo-animate-fade"
            style={{ maxWidth: "500px", width: "90%" }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className="repo-modal-close"
              onClick={() =>
                setMoveModal({ show: false, items: [], targetFolderId: null })
              }
            >
              <X size={20} />
            </button>
            <h3>
              Move{" "}
              {moveModal.items.length === 1
                ? "Item"
                : `${moveModal.items.length} Items`}
            </h3>
            <p>
              Select destination folder for{" "}
              <strong>
                {moveModal.items.length === 1
                  ? moveModal.items[0].name
                  : "selected items"}
              </strong>
            </p>

            <div className="repo-folder-tree">
              <div
                className={`repo-folder-option ${moveModal.targetFolderId === null ? "selected" : ""}`}
                onClick={() =>
                  setMoveModal((prev) => ({ ...prev, targetFolderId: null }))
                }
              >
                <Folder size={16} /> Root Directory
              </div>

              {/* Recursive folder render */}
              {(() => {
                const renderFolders = (parentId = null, depth = 0) => {
                  const folders = items.filter((i) => {
                    if (i.type !== "folder" || i.parentId !== parentId || moveModal.items.some((mi) => mi.id === i.id)) return false;
                    
                    if (repoView === "personal_space") {
                      return i.isPersonalSpace === true;
                    }
                    if (repoView === "global") {
                      return i.ownerType === "admin" || i.mode === "global" || i.mode === "group";
                    }
                    if (repoView === "personal") {
                      return !i.isPersonalSpace && i.ownerType !== "admin" && i.mode !== "global" && i.mode !== "group";
                    }
                    return false;
                  });
                  return folders.map((f) => (
                    <React.Fragment key={f.id}>
                      <div
                        className={`repo-folder-option ${moveModal.targetFolderId === f.id ? "selected" : ""}`}
                        style={{ paddingLeft: `${(depth + 1) * 20 + 12}px` }}
                        onClick={() =>
                          setMoveModal((prev) => ({
                            ...prev,
                            targetFolderId: f.id,
                          }))
                        }
                      >
                        <Folder size={16} /> {f.name}
                      </div>
                      {renderFolders(f.id, depth + 1)}
                    </React.Fragment>
                  ));
                };
                return renderFolders(null, 0);
              })()}
            </div>

            <div
              className="repo-modal-actions"
              style={{
                display: "flex",
                justifyContent: "space-between",
                flexDirection: "row",
              }}
            >
              <button
                className="repo-btn repo-btn-primary"
                onClick={() => {
                  handleMove(moveModal.items, moveModal.targetFolderId);
                  setMoveModal({
                    show: false,
                    items: [],
                    targetFolderId: null,
                  });
                  setSelectedItems(new Set());
                }}
              >
                Save
              </button>
              <button
                className="repo-btn"
                onClick={() =>
                  setMoveModal({ show: false, items: [], targetFolderId: null })
                }
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* New Folder Modal */}
      {createFolderModal.show && (
        <div
          className="repo-modal-overlay"
          onClick={() => setCreateFolderModal({ show: false, name: "" })}
        >
          <div
            className="repo-modal repo-animate-fade"
            style={{ maxWidth: "400px" }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className="repo-modal-close"
              onClick={() => setCreateFolderModal({ show: false, name: "" })}
            >
              <X size={20} />
            </button>
            <h3>New Folder</h3>
            <div className="repo-modal-body" style={{ marginTop: "1.5rem" }}>
              <input
                type="text"
                className="repo-search-input"
                style={{ width: "100%", padding: "0.8rem 1rem" }}
                placeholder="Folder Name"
                autoFocus
                value={createFolderModal.name}
                onChange={(e) =>
                  setCreateFolderModal((prev) => ({
                    ...prev,
                    name: e.target.value,
                  }))
                }
                onKeyDown={(e) => e.key === "Enter" && handleCreateFolder()}
              />
            </div>
            <div
              className="repo-modal-actions"
              style={{
                marginTop: "1.5rem",
                display: "flex",
                justifyContent: "flex-end",
              }}
            >
              <button
                className="repo-btn repo-btn-primary d-flex align-items-center justify-content-center"
                onClick={handleCreateFolder}
              >
                Create Folder
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Mode Selection Modal (First Modal) */}
      {showFirstModal && (
        <div className="repo-modal-overlay" onClick={() => setShowFirstModal(false)}>
          <div
            className="repo-modal repo-animate-fade text-center"
            style={{ maxWidth: "450px" }}
            onClick={(e) => e.stopPropagation()}
          >
            <button className="repo-modal-close" onClick={() => setShowFirstModal(false)}>
              <X size={20} />
            </button>
            
            <div className="d-flex justify-content-center mb-3">
              <div
                className="rounded-circle d-flex justify-content-center align-items-center"
                style={{ width: "60px", height: "60px", backgroundColor: "rgba(255,255,255,0.1)" }}
              >
                <FileIcon size={30} />
              </div>
            </div>

            <p className="mb-2">
              You have uploaded document. You are currently in <strong>{uploadMode} Mode</strong>.
            </p>
            <p className="mb-4 text-muted" style={{ fontSize: "0.9rem" }}>
              You can change the mode below if needed:
            </p>

            <div className="d-flex justify-content-center gap-4 mb-4">
              {["Global", "Personal"].map((mode) => (
                <label key={mode} className="repo-radio-label" style={{ cursor: "pointer" }}>
                  <input
                    type="radio"
                    name="uploadMode"
                    value={mode}
                    checked={uploadMode === mode}
                    onChange={() => setUploadMode(mode)}
                    style={{ marginRight: "8px" }}
                  />
                  {mode}
                </label>
              ))}
            </div>

            <div className="repo-modal-actions justify-content-center gap-3">
              <button className="repo-btn" onClick={() => setShowFirstModal(false)}>
                Cancel
              </button>
              <button
                className="repo-btn repo-btn-primary"
                onClick={() => {
                  setShowFirstModal(false);
                  setShowTrainingModal(true);
                }}
              >
                Continue
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Training Confirmation Modal */}
      {showTrainingModal && (
        <div className="repo-modal-overlay" onClick={() => !isTraining && setShowTrainingModal(false)}>
          <div
            className="repo-modal repo-animate-fade text-center"
            style={{ maxWidth: "450px" }}
            onClick={(e) => e.stopPropagation()}
          >
            <button className="repo-modal-close" onClick={() => !isTraining && setShowTrainingModal(false)}>
              <X size={20} />
            </button>
            
            <div className="d-flex justify-content-center mb-3">
              <div
                className="rounded-circle d-flex justify-content-center align-items-center"
                style={{ width: "80px", height: "80px", backgroundColor: "rgba(255,255,255,0.05)", boxShadow: "0 0 15px rgba(0,0,0,0.2)" }}
              >
                <img src={changeStatusIcon} alt="Training" style={{ width: "40px", height: "40px" }} />
              </div>
            </div>

            <p className="fw-bold mb-4">
              Are you sure you want to send this document for training?
            </p>

            <div className="repo-modal-actions justify-content-center gap-3">
              <button className="repo-btn" disabled={isTraining} onClick={() => setShowTrainingModal(false)}>
                Cancel
              </button>
              <button
                className="repo-btn repo-btn-primary"
                disabled={isTraining}
                onClick={() => handleSendForTraining(selectedFileToTrain, uploadMode)}
              >
                {isTraining ? "Sending..." : "Send"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Summary Review Modal */}
      {showSummaryModal && (
        <div className="repo-modal-overlay" onClick={() => setShowSummaryModal(false)}>
          <div
            className="repo-modal repo-animate-fade"
            style={{ maxWidth: "800px", width: "90%" }}
            onClick={(e) => e.stopPropagation()}
          >
            <button className="repo-modal-close" onClick={() => setShowSummaryModal(false)}>
              <X size={20} />
            </button>
            
            <h3 className="text-center mb-4">Review Summaries</h3>

            <div className="row mb-4">
              <div className="col-md-6">
                <label className="repo-filter-label">Data Name</label>
                <input
                  type="text"
                  className="repo-search-input w-100"
                  placeholder="Enter Data Name"
                  value={dataName}
                  onChange={(e) => setDataName(e.target.value)}
                />
              </div>
              <div className="col-md-6">
                <label className="repo-filter-label">Upload Mode</label>
                <div className="dropdown w-100" ref={dropdownRef}>
                  <button
                    className="repo-search-input w-100 text-start d-flex justify-content-between align-items-center"
                    onClick={() => setShowUploadModeDropdown(!showUploadModeDropdown)}
                  >
                    {uploadMode}
                    {showUploadModeDropdown ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                  </button>
                  {showUploadModeDropdown && (
                    <div className="dropdown-menu show w-100 shadow-lg border-0 bg-dark text-white">
                      {["Global", "Personal"].map((mode) => (
                        <button
                          key={mode}
                          className="dropdown-item text-white"
                          onClick={() => {
                            setUploadMode(mode);
                            setShowUploadModeDropdown(false);
                          }}
                        >
                          {mode}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="repo-summary-list" style={{ maxHeight: "400px", overflowY: "auto" }}>
              {summaryDataList.map((item, index) => (
                <div key={index} className="repo-summary-card p-3 mb-3" style={{ background: "rgba(255,255,255,0.05)", borderRadius: "8px" }}>
                  <div className="d-flex align-items-center gap-2 mb-2">
                    <FileIcon size={18} />
                    <strong>{item.filename}</strong>
                  </div>
                  <div className="text-muted" style={{ fontSize: "0.9rem" }}>
                    {item.summary || "No summary available."}
                  </div>
                </div>
              ))}
            </div>

            <div className="repo-modal-actions justify-content-center gap-3 mt-4">
              <button className="repo-btn" onClick={() => setShowSummaryModal(false)}>
                Cancel
              </button>
              <button
                className="repo-btn repo-btn-primary"
                onClick={uploadToBackend}
                disabled={isSubmitting}
              >
                {isSubmitting ? "Submitting..." : "Complete Training"}
              </button>
            </div>
          </div>
        </div>
      )}
    </DndProvider>
  );
};

export default RepoOrganizer;
